import { Controller } from "@hotwired/stimulus";
import { debounce } from "../utils/debounce";

// Connects to data-controller="cep-field"
// People type a CEP three ways: 00000000, 00000-000 and 00.000-000. This
// normalizes whatever they type to a single shape as they go — so the field's
// own pattern and maxlength never fight the way someone writes — and submits
// the form on its own once eight digits are there, instead of making them
// reach for "Calcular".
//
// Not to be confused with `cep-lookup`, which answers a different question on
// the checkout/subscription forms: it calls /loja/cep/:digits and fills the
// address fields. This one masks a field and submits its own form.
export default class extends Controller {
	static targets = ["input"];
	static values = { delay: { type: Number, default: 400 } };

	connect() {
		// Whatever is already in the field was already quoted (the frame renders
		// with the stored CEP), so it must not re-submit on connect — but it does
		// need masking: the server stores and echoes bare digits, so without this
		// the field loses its shape every time the frame comes back.
		this.lastSubmitted = this.digits();
		this.scheduleSubmit = debounce(this.submitIfComplete.bind(this), this.delayValue);
		this.mask();
	}

	format() {
		this.mask();
		this.scheduleSubmit();
	}

	mask() {
		const digits = this.digits();
		this.inputTarget.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
	}

	// Debounced, and only for a value that actually changed: the quoting endpoint
	// is rate limited, and holding a key down must not spend that budget.
	submitIfComplete() {
		const digits = this.digits();
		if (digits.length !== 8 || digits === this.lastSubmitted) return;

		this.lastSubmitted = digits;
		this.element.requestSubmit();
	}

	digits() {
		return this.inputTarget.value.replace(/\D/g, "").slice(0, 8);
	}
}
